<script setup lang="ts">
import { Ref, ref, watchEffect } from 'vue';
import { SearchFilter, FilterOption } from '../../../search-panel-types';
import FilterFieldLayout from './FilterFieldLayout.vue';
import FilterChip from './FilterChip.vue';
import useLocale from '../../../composables/useLocale';
import SearchByFilterOptions from './SearchByFilterOptions.vue';

type Props = {
  fieldKey: string;
  items: FilterOption[];
  label: string;
  selected: { value: SearchFilter };
};

const { t } = useLocale();
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'filters-updated', filter: SearchFilter): void;
  (e: 'filter-removed', filterKey: string, value: FilterOption): void;
}>();
const selectedInternal: Ref<FilterOption[]> = ref([]);
const defaultShowedOptions = 4;
const showedOptions = ref(defaultShowedOptions);

watchEffect(() => {
  if (!props.selected.value[props.fieldKey]) {
    selectedInternal.value = [];
    return;
  }
  selectedInternal.value = props.selected.value[props.fieldKey];
});

const isSelected = (value: string): boolean => !!selectedInternal.value.find((el) => el.value === value);

const handleSelect = (item: FilterOption) => {
  if (isSelected(item.value)) {
    return;
  }
  selectedInternal.value.push(item);
  emit('filters-updated', { [props.fieldKey]: selectedInternal.value });
};

const handleRemoveSelected = (item: FilterOption) => {
  selectedInternal.value = selectedInternal.value.filter((el) => el.value !== item.value);
  emit('filter-removed', props.fieldKey, item);
};

const isShowedAllOptions = (): boolean => showedOptions.value === props.items.length;

const toggleShowedFilters = () => {
  showedOptions.value = isShowedAllOptions() ? defaultShowedOptions : props.items.length;
};

const update = (value: string) => {
  if (!value) {
    return;
  }
  const filter = props.items.find((item) => item.value === value);

  if (!filter) {
    return;
  }
  handleSelect(filter);
};
</script>

<template>
  <FilterFieldLayout v-show="props.items?.length > 0" :label="props.label ?? ''">
    <template v-if="props.items?.length > 0" #content>
      <SearchByFilterOptions
        v-if="props.items.length > defaultShowedOptions"
        @update="update"
        :items="props.items"
        :id="props.label"
      />
      <div class="d-flex flex-wrap mb-2">
        <div v-for="item in props.items?.slice(0, showedOptions)" :key="item.label">
          <FilterChip
            v-if="item.count > 0"
            class="mb-0"
            :key="item.value"
            :is-selected="isSelected(item.value)"
            :label="item.label"
            :count="item.count"
            @click:close="handleRemoveSelected(item)"
            @click="handleSelect(item)"
          />
        </div>
      </div>
      <div v-if="props.items?.length > defaultShowedOptions">
        <v-btn
          v-if="!isShowedAllOptions()"
          variant="text"
          color="primary"
          class="text-none"
          @click="toggleShowedFilters"
        >
          {{ t('button_show_all') }}
          <v-icon>mdi-plus</v-icon>
        </v-btn>
        <v-btn v-else variant="text" color="primary" class="text-none" @click="toggleShowedFilters">
          {{ t('label_readmore_less') }}
          <v-icon>mdi-minus</v-icon>
        </v-btn>
      </div>
    </template>
  </FilterFieldLayout>
</template>

<style scoped lang="scss">
.v-list {
  padding: 0;
  max-height: 400px;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 3.5px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3.5px;
    background-color: #a2a2a2;
    height: 110px;
  }
}
.v-list-item__content > * {
  flex: auto;
}
.v-list-item__content {
  justify-content: space-between;
}
.v-list-item {
  border-bottom: 1px solid #b9b9b9;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    .v-list-item__title {
      color: var(--v-primary-base);
    }
  }
}
.v-list-item--link:before {
  background-color: var(--v-primary-base);
}
</style>
